import {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useContext,
  useRef,
} from 'react';
import { useFormik, FormikConfig, FormikValues } from 'formik';

import usePrevious from 'hooks/usePrevious';
import useStoredBooking, { getInitialValues } from 'hooks/useStoredBooking';
import useUpdateEffect from 'hooks/useUpdateEffect';

import useBooking from 'hooks/useBooking';

import { BasketContext } from 'state/basket';

import {
  generateBookingItemFields,
  combineSlots,
  combineBookingItems,
  getBookingState,
} from 'utils/booking';

import { formatBasketPayload } from '../../services/formatBasketPayload';
import { formatUpdateBookingPayload } from '../../services/formatUpdateBookingPayload';

import { getValidator } from '../../validators/booking';

interface Service {
  id: number;
}

interface Options {
  services?: Service[];
}

const useFormikBooking = (
  enabledOption?: boolean = true,
  {
    initialValues,
    enableReinitialize: enableReinitializeOption = false,
    onSubmit: onSubmitOption = () => null,
    ...options
  }: FormikConfig<FormikValues> = {},
  enableSubmission?: boolean = false,
  { services }: Options = {}
) => {
  const [changesSaved, setChangesSaved] = useState<boolean>(false);

  const [
    storedValues,
    setStorage,
    removeStorage,
    storageInitialising,
    receivedUpdate,
  ] = useStoredBooking(initialValues);

  const [formValues, setFormValues] = useState(storedValues);

  useEffect(() => {
    setFormValues(storedValues);
  }, [storedValues]);

  const enabled = useMemo(
    () =>
      enabledOption instanceof Function
        ? enabledOption(storedValues)
        : !!enabledOption,
    [enabledOption, storedValues]
  );

  const { actions: { submitBookingToBasket, updateBooking } = {} } = useContext(
    BasketContext
  );

  const { data: existingBooking } = useBooking(
    storedValues.id ? Number(storedValues.id) : undefined,
    enableSubmission
  );

  const bookingState = useMemo(() => getBookingState(existingBooking), [
    existingBooking,
  ]);

  const onSubmit = async (values, helpers) => {
    const extraData = { existingBooking };

    const {
      id,
      venueId,
      spaceIds,
      date,
      slots,
      service,
      host: { value: host } = {},
      recurrence = {},
      bookingItems = {},
    } = values;

    const { setSubmitting } = helpers;

    const submitBooking = payload =>
      id
        ? updateBooking({ id }, formatUpdateBookingPayload(payload))
        : submitBookingToBasket(formatBasketPayload(payload));

    await submitBooking({
      date: new Date(date),
      venueId,
      spaceIds,
      slots: combineSlots(slots),
      clientServiceId: service,
      bookingHostId: host,
      ...(recurrence.enabled ? { recurrence } : {}),
      bookingItems: combineBookingItems(bookingItems),
      initialBookingItems: getInitialValues({
        ...(existingBooking || {}),
        clientService: undefined,
        recurrenceRules: undefined,
      }).bookingItems,
    });

    try {
      return await Promise.resolve(onSubmitOption(values, helpers, extraData));
    } finally {
      setSubmitting(false);
      setChangesSaved(true);
      removeStorage();
    }
  };

  // Reinitialise Formik exactly once after storage has initialised.
  const initialising = usePrevious<boolean>(storageInitialising, true);
  const updatesEnabled = !initialising;
  const enableReinitialize =
    enableReinitializeOption || initialising || receivedUpdate;

  const formik = useFormik({
    ...options,
    initialValues: initialising ? storedValues : formValues,
    enableReinitialize,
    validate: getValidator({ services, state: bookingState }),
    onSubmit: enableSubmission ? onSubmit : () => null,
  });

  const { values, setFieldValue, resetForm } = formik;

  const sendUpdatesRef = useRef<boolean>(false);

  useEffect(() => {
    sendUpdatesRef.current = !initialising && !receivedUpdate;
  }, [initialising, receivedUpdate]);

  useEffect(() => {
    if (sendUpdatesRef.current) {
      setChangesSaved(false);

      if (values?.venueId !== undefined && enabled) {
        setStorage(values);
      } else if (!enabled) {
        removeStorage();
      }
    }

    sendUpdatesRef.current = true;
  }, [enabled, values, setStorage, removeStorage]);

  const generateBookingItems = useCallback(() => {
    const numericFields = [
      'everyNumberOf',
      'nthDayOfMonth',
      'weekNumber',
      'count',
    ];

    const numberOrUndefined = value => {
      const number = Number(value);
      return Number.isNaN(number) || value === '' ? undefined : number;
    };

    return generateBookingItemFields(
      values.date,
      values.spaceIds,
      values.slots,
      values.recurrence.enabled
        ? Object.keys(values.recurrence)
            .map(key => ({
              [key]: numericFields.includes(key)
                ? numberOrUndefined(values.recurrence[key])
                : values.recurrence[key],
            }))
            .reduce((prev, curr) => ({ ...prev, ...curr }), {})
        : undefined
    );
  }, [values.date, values.spaceIds, values.slots, values.recurrence]);

  useUpdateEffect(
    () => {
      if (!values.recurrence.enabled) {
        setFieldValue('bookingItems', generateBookingItems());
      }
    },
    [values.recurrence.enabled, generateBookingItems, setFieldValue],
    updatesEnabled
  );

  useUpdateEffect(
    () => {
      setFieldValue('recurrence.checked', false);
    },
    [setFieldValue, values.spaceIds, values.slots],
    updatesEnabled
  );

  const clearAll = useCallback(
    async (...args) => {
      await resetForm({ values: getInitialValues(...args) });
      removeStorage();
    },
    [resetForm, removeStorage]
  );

  const combinedSlots = useMemo(() => combineSlots(values.slots), [
    values.slots,
  ]);

  return {
    formik,
    initialising,
    existingBooking,
    changesSaved,
    setChangesSaved,
    generateBookingItems,
    clearAll,
    removeStorage,
    combinedSlots,
  };
};

export default useFormikBooking;
